class ProductInterestSignup extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.trigger = this.querySelector('.product-interest-signup__trigger');
    this.panel = this.querySelector('.product-interest-signup__panel');
    this.form = this.querySelector('form');
    this.tagsInput = this.querySelector('[data-interest-tags]');
    this.returnToInput = this.querySelector('[data-interest-return-to]');
    this.consent = this.querySelector('[data-interest-consent]');
    this.variantsScript = this.querySelector('[data-interest-variants]');
    this.successMessage = this.querySelector('[data-interest-success-message]');
    this.formError = this.querySelector('[role="alert"]');
    this.heading = this.querySelector('.product-interest-signup__intro h3');
    this.description = this.querySelector('.product-interest-signup__intro p');
    this.triggerText = this.trigger?.querySelector('span');
    this.pricingElements = Array.from(document.querySelectorAll('[data-interest-pricing-section]'))
      .filter((element) => element.dataset.interestPricingSection === this.dataset.sectionId);

    // Keep the confirmation outside the collapsible panel and above the trigger.
    // After success, keep the trigger available for corrections/resubmission, but
    // give it a muted visual state so the confirmation is easier to notice.
    if (this.successMessage && this.trigger) {
      this.insertBefore(this.successMessage, this.trigger);
    }

    this.productFormElement = document.getElementById(this.dataset.formId);
    this.purchaseArea = this.productFormElement?.closest('product-form');
    this.quantityArea = document.getElementById(`Quantity-Form-${this.dataset.sectionId}`);
    this.availabilityLabel = this.quantityArea?.querySelector('[data-interest-availability]');
    this.variants = [];
    this.pendingStorageKey = `hsr-interest-pending-${this.dataset.productId}-${this.dataset.sectionId}`;
    this.completedStorageKey = `hsr-interest-completed-${this.dataset.productId}`;

    try {
      this.variants = JSON.parse(this.variantsScript?.textContent || '[]');
    } catch (error) {
      this.variants = [];
    }

    let pendingSubmission = false;
    try {
      const rawSubmission = sessionStorage.getItem(this.pendingStorageKey);
      if (rawSubmission) {
        const submission = JSON.parse(rawSubmission);
        pendingSubmission = Number.isFinite(submission.at) && Date.now() - submission.at < 180000;
        sessionStorage.removeItem(this.pendingStorageKey);
      }
    } catch (error) {
      pendingSubmission = false;
    }

    // Only treat a pending submission as successful if Shopify did not return form errors.
    const hasServerSuccess = Boolean(this.successMessage && !this.successMessage.hidden);
    if (this.formError) pendingSubmission = false;

    this.completed = this.dataset.customerHasInterest === 'true';
    try {
      if (localStorage.getItem(this.completedStorageKey) === 'true') this.completed = true;
    } catch (error) {
      // Customer-tag detection and server success still work if browser storage is blocked.
    }

    if (!this.formError && (hasServerSuccess || pendingSubmission)) {
      this.completed = true;
      try {
        localStorage.setItem(this.completedStorageKey, 'true');
      } catch (error) {
        // The submitted customer profile remains tagged even if local storage is unavailable.
      }
    }

    this.trigger?.addEventListener('click', () => this.togglePanel());
    this.trigger?.classList.toggle('is-completed', this.completed);

    this.handleVariantChange = (event) => {
      if (!event.target.matches(`#${CSS.escape(this.dataset.formId)} input[name="id"]`)) return;
      this.updateForVariant(event.target.value);
    };
    document.addEventListener('change', this.handleVariantChange);

    // Timesact can change or replace purchase-button markup after initial page load.
    // Re-evaluate the active variant whenever its button/description markup changes.
    // This is a safety net for detecting an active Timesact pre-order before hiding
    // the purchase area. It does not depend on the app embed being switched on/off.
    if (this.purchaseArea && window.MutationObserver) {
      this.timesactObserver = new MutationObserver(() => {
        const currentVariantId = this.productFormElement?.querySelector('input[name="id"]')?.value;
        if (currentVariantId) this.updateForVariant(currentVariantId);
      });
      this.timesactObserver.observe(this.purchaseArea, {
        subtree: true,
        childList: true,
        characterData: true
      });
    }

    // Remember the selected variant through Shopify's native form redirect.
    this.form?.addEventListener('submit', () => {
      const selectedVariantId = this.productFormElement?.querySelector('input[name="id"]')?.value;
      if (selectedVariantId && this.returnToInput) {
        this.returnToInput.value = `${window.location.pathname}?variant=${encodeURIComponent(selectedVariantId)}`;
      }
      try {
        sessionStorage.setItem(this.pendingStorageKey, JSON.stringify({ at: Date.now(), variantId: selectedVariantId || null }));
      } catch (error) {
        // The Shopify form still submits normally if session storage is unavailable.
      }
    });

    const variantInput = this.productFormElement?.querySelector('input[name="id"]');
    this.updateForVariant(variantInput?.value);

    if (this.formError && !this.completed) {
      this.panel.hidden = false;
      this.trigger.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
    }
  }

  disconnectedCallback() {
    if (this.handleVariantChange) document.removeEventListener('change', this.handleVariantChange);
    if (this.timesactObserver) this.timesactObserver.disconnect();
  }

  getPreOrderOverride(variant) {
    return String(variant?.pre_order_override ?? '').trim().toLowerCase();
  }

  isTimesactPreorderActive(variant) {
    // A product tagged pre-order remains under Timesact control unless the
    // selected variant explicitly sets custom.pre_order_override to one of
    // the two supported override values.
    if (this.dataset.excluded === 'true') {
      return !['coming soon', 'sold out'].includes(this.getPreOrderOverride(variant));
    }
    if (!this.purchaseArea) return false;

    // For other products, leave a live Timesact preorder state untouched.
    if (this.purchaseArea.querySelector(
      '.timesact-preorder-description, .timesact-selling-plan-description, .timesact-button-po, [data-timesact-selling-plan], #preorder-label'
    )) return true;

    const buttons = this.purchaseArea.querySelectorAll('button, input[type="submit"], [role="button"]');
    return Array.from(buttons).some((button) => {
      const label = (button.textContent || button.value || '').replace(/\s+/g, ' ').trim();
      return /^pre[\s-]?order(?:\b|$)/i.test(label);
    });
  }

  updateCopy(isComingSoon) {
    const copy = isComingSoon
      ? {
          sourceTag: 'interest_comingsoon',
          heading: 'Be the first to know',
          description: 'Get updates about this release.',
          button: 'GET RELEASE UPDATES',
          success: "You're on the list. We'll keep you updated about this release."
        }
      : {
          sourceTag: 'interest_soldout',
          heading: 'Stay in the loop',
          description: 'Discover future releases from this collection and other related editions from Human Sweat Records.',
          button: 'KEEP ME INFORMED',
          success: "You're on the list. We'll keep you informed about future releases from this collection."
        };
    this.dataset.currentSourceTag = copy.sourceTag;
    if (this.heading) this.heading.textContent = copy.heading;
    if (this.description) this.description.textContent = copy.description;
    if (this.triggerText) this.triggerText.textContent = copy.button;
    if (this.successMessage) this.successMessage.textContent = copy.success;
  }

  updatePricingForComingSoon(shouldHide) {
    this.pricingElements.forEach((element) => {
      element.hidden = shouldHide;
    });
  }

  updateAvailability(visible, text) {
    if (!this.quantityArea) return;
    this.quantityArea.classList.toggle('is-interest-unavailable', visible);
    if (this.availabilityLabel) {
      this.availabilityLabel.textContent = text;
      this.availabilityLabel.hidden = !visible;
      this.availabilityLabel.classList.toggle('is-coming-soon', text === 'COMING SOON');
      this.availabilityLabel.classList.toggle('is-sold-out', text === 'SOLD OUT');
    }
  }

  togglePanel() {
    const isOpen = this.trigger.getAttribute('aria-expanded') === 'true';
    this.trigger.setAttribute('aria-expanded', String(!isOpen));
    this.panel.hidden = isOpen;
    if (!isOpen) this.panel.querySelector('input[type="text"]')?.focus();
  }

  updateForVariant(variantId) {
    const variant = this.variants.find((item) => String(item.id) === String(variantId));
    if (!variant) {
      this.hidden = true;
      if (this.purchaseArea) this.purchaseArea.hidden = false;
      this.updateAvailability(false, '');
      this.updatePricingForComingSoon(false);
      if (this.panel) this.panel.hidden = true;
      if (this.successMessage) this.successMessage.hidden = true;
      this.trigger?.setAttribute('aria-expanded', 'false');
      return;
    }

    const isPreorderTaggedProduct = this.dataset.excluded === 'true';
    const variantPreOrderOverride = this.getPreOrderOverride(variant);
    const hasExplicitPreorderOverride = isPreorderTaggedProduct && ['coming soon', 'sold out'].includes(variantPreOrderOverride);

    if (this.isTimesactPreorderActive(variant)) {
      this.hidden = true;
      if (this.purchaseArea) this.purchaseArea.hidden = false;
      this.updateAvailability(false, '');
      this.updatePricingForComingSoon(false);
      if (this.panel) this.panel.hidden = true;
      if (this.successMessage) this.successMessage.hidden = true;
      this.trigger?.setAttribute('aria-expanded', 'false');
      return;
    }

    // A valid custom.pre_order_override value on a pre-order product overrides
    // Timesact for this selected variant only. Empty/other values leave Timesact
    // in control. Standard non-pre-order products keep the existing product-tag rules.
    const isComingSoon = hasExplicitPreorderOverride
      ? variantPreOrderOverride === 'coming soon'
      : this.dataset.comingSoon === 'true';
    const shouldShow = hasExplicitPreorderOverride || Number(variant.inventory_quantity) <= 0;
    this.updateCopy(isComingSoon);
    this.updatePricingForComingSoon(hasExplicitPreorderOverride && isComingSoon);

    this.hidden = !shouldShow;
    if (this.purchaseArea) this.purchaseArea.hidden = shouldShow;
    const availabilityText = isComingSoon ? 'COMING SOON' : 'SOLD OUT';
    this.updateAvailability(shouldShow, availabilityText);

    if (!shouldShow) {
      if (this.panel) this.panel.hidden = true;
      if (this.successMessage) this.successMessage.hidden = true;
      if (this.trigger) this.trigger.hidden = false;
      this.trigger?.setAttribute('aria-expanded', 'false');
      return;
    }

    const sourceTag = isComingSoon ? 'interest_comingsoon' : 'interest_soldout';
    if (this.tagsInput) {
      this.tagsInput.value = `newsletter,email_signup_10,${sourceTag},${this.dataset.productTag},interest_variant_${variant.id}`;
    }
    if (this.returnToInput) {
      this.returnToInput.value = `${window.location.pathname}?variant=${encodeURIComponent(variant.id)}`;
    }

    this.trigger.hidden = false;
    this.trigger.classList.toggle('is-completed', this.completed);
    this.trigger.setAttribute('aria-expanded', this.panel.hidden ? 'false' : 'true');
    if (this.successMessage) this.successMessage.hidden = !this.completed;
  }

}

if (!customElements.get('product-interest-signup')) {
  customElements.define('product-interest-signup', ProductInterestSignup);
}
